import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

import { fileImageSrcSet } from '../../lib/file-image';
import { cn } from '../../lib/utils';
import { Button } from '../shadcn/button';

/** Past this many pixels of travel, letting go of a swipe moves a picture. */
const SWIPE_THRESHOLD = 50;

export type GalleryImage = {
  src: string;
  /**
   * What the picture shows. Omit it only when the image is decorative — the
   * viewer then renders `alt=""`, which is the correct markup for "skip this",
   * and is why there is no invented fallback here. A made-up description is
   * worse than none: a screen reader reads it as fact.
   */
  alt?: string;
};

/**
 * Widens the loose form a caller may pass — a bare URL — into the shape these
 * components work in.
 *
 * Declared here rather than in `ImageGallery` so the dependency runs one way:
 * the gallery is built out of the viewer, the viewer knows nothing about the
 * gallery, and neither file has to import the other at runtime.
 */
export function toGalleryImage(image: string | GalleryImage): GalleryImage {
  return typeof image === 'string' ? { src: image } : image;
}

export type ImageLightboxProps = {
  /** Plain URLs, or `{ src, alt }` when the pictures carry meaning. */
  images: ReadonlyArray<string | GalleryImage>;
  /**
   * Which picture is open, or `null` for closed.
   *
   * Controlled, and deliberately an index rather than a boolean: the thing that
   * opens the viewer is always "this one", and a separate `open` flag beside an
   * index is two pieces of state that can disagree about whether picture 4 is
   * showing.
   */
  activeIndex: number | null;
  onClose: () => void;
  onIndexChange: (next: number) => void;
  /**
   * Names the viewer for anyone who cannot see it — "MDS café hall", not
   * "gallery". Required for the same reason `ImageCarousel` requires one: a
   * dialog with no name is announced as "dialog" and nothing else.
   */
  label: string;
  className?: string;
};

/**
 * A full-screen viewer for one picture at a time, with its neighbours an arrow
 * key, a swipe or a click away.
 *
 * Radix's Dialog underneath, which is what supplies the focus trap, the Esc
 * key, the scroll lock and the `aria-modal` wiring — the same machinery `Sheet`
 * sits on. Only the parts Radix has no opinion about are hand-written here: the
 * arrow keys, the swipe, and closing on a click into the empty space around the
 * photograph.
 *
 * Alt text is never invented. A picture that arrived without one renders
 * `alt=""`, exactly as it does in the carousel, because "Image 3 of 8" read
 * aloud as though it described the photograph is worse than silence.
 */
export function ImageLightbox({
  images,
  activeIndex,
  onClose,
  onIndexChange,
  label,
  className,
}: ImageLightboxProps) {
  const slides = React.useMemo(() => images.map(toGalleryImage), [images]);
  const count = slides.length;

  const swipeStartX = React.useRef<number | null>(null);

  const go = React.useCallback(
    (next: number) => {
      if (count > 0) onIndexChange(((next % count) + count) % count);
    },
    [count, onIndexChange],
  );

  // An index left past the end of a list that has since got shorter would
  // otherwise render a blank viewer over the page with no way to tell why.
  const open = activeIndex !== null && activeIndex >= 0 && activeIndex < count;
  const image = open ? slides[activeIndex] : undefined;

  if (!open || !image) return null;

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(activeIndex! - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(activeIndex! + 1);
    }
  }

  function onTouchStart(event: React.TouchEvent) {
    swipeStartX.current = event.touches[0]?.clientX ?? null;
  }

  function onTouchEnd(event: React.TouchEvent) {
    const start = swipeStartX.current;
    swipeStartX.current = null;
    if (start === null) return;

    const travelled = (event.changedTouches[0]?.clientX ?? start) - start;
    if (Math.abs(travelled) < SWIPE_THRESHOLD) return;
    go(activeIndex! + (travelled > 0 ? -1 : 1));
  }

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-(--z-modal) bg-(--lightbox-scrim) backdrop-blur-xs',
            'animate-overlay-in motion-reduce:animate-none',
          )}
        />

        {/* The content layer is the whole viewport rather than a box around the
            picture, so the arrow keys work wherever focus has landed and the
            empty space beside the photograph is clickable.

            That is also why closing is `target === currentTarget` rather than
            Radix's outside-press: nothing is ever outside a layer that covers
            the screen. Comparing the two means a click on the picture or on a
            control is a click on that thing, and a click on the dark is a click
            on the dark — without a `stopPropagation` on every child. */}
        <DialogPrimitive.Content
          aria-label={label}
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
          className={cn(
            'fixed inset-0 z-(--z-modal) flex items-center justify-center',
            'text-(--lightbox-fg) outline-none',
            // The picture is never allowed to reach an edge. This padding is
            // what reserves the dark: the controls sit 16px in and are 36px
            // across, so 56px is the first value that keeps every one of them
            // clear of the photograph rather than resting on it. Sizing the
            // image at `90vw` instead left a landscape shot running under the
            // close button, where a dark glyph on a dark corner of a picture is
            // a control you cannot find.
            //
            // It also gives the click-to-close its target: with the image
            // filling the viewport there was no dark left to click.
            'p-14 sm:p-20',
            // The dark is a control — a click on it closes — so it says so.
            // The controls inside keep their pointer: the base layer matches
            // them directly, which beats inheriting this.
            'cursor-zoom-out',
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>

          {/* "1 / 1" counts nothing, so a single picture goes without. */}
          {count > 1 && (
            <span
              className={cn(
                'absolute top-4 left-4 rounded-button bg-(--lightbox-chip-bg)',
                'px-3 py-1 font-mono text-2xs',
              )}
            >
              {activeIndex + 1} / {count}
            </span>
          )}

          <LightboxControl label="Close gallery" onClick={onClose} className="top-4 right-4">
            <X />
          </LightboxControl>

          {count > 1 && (
            <>
              <LightboxControl
                label="Previous image"
                onClick={() => go(activeIndex - 1)}
                className="left-4 sm:left-6 sm:size-12"
              >
                <ChevronLeft />
              </LightboxControl>

              <LightboxControl
                label="Next image"
                onClick={() => go(activeIndex + 1)}
                className="right-4 sm:right-6 sm:size-12"
              >
                <ChevronRight />
              </LightboxControl>
            </>
          )}

          <img
            // Keyed by index so swapping pictures remounts the element. Without
            // it the browser keeps showing the previous frame until the next
            // one has decoded, which on a slow connection reads as the arrow
            // key having done nothing.
            key={activeIndex}
            src={image.src}
            srcSet={fileImageSrcSet(image.src)}
            sizes="100vw"
            alt={image.alt ?? ''}
            decoding="async"
            draggable={false}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            // Bounded by the padded box rather than by the viewport, so the
            // reserved dark above is never eaten into. `object-contain` keeps
            // the aspect ratio inside whichever of the two the picture hits
            // first, so a tall photograph and a wide one both fit whole.
            //
            // Not zoom-out: a click here is the one click in the viewer that
            // does nothing, so it must not inherit the backdrop's promise.
            className="max-h-full max-w-full cursor-default object-contain"
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * One of the viewer's round controls. They differ only in glyph, label and
 * corner, and every one of them needs the same six classes to read on the
 * scrim — which is a component rather than a repeated `className`.
 */
function LightboxControl({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'absolute rounded-full text-inherit',
        'hover:bg-(--lightbox-control-hover-bg) hover:text-inherit',
        'focus-visible:border-focus-on-panel focus-visible:ring-focus-on-panel/50',
        className,
      )}
    >
      {children}
    </Button>
  );
}
